/* eslint-disable no-undef */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { push } from 'connected-react-router';
import assign from 'lodash/assign';

import Button from '@zg-rentals/multibrand-ui/lib/Button';
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Container from '@zg-rentals/multibrand-ui/lib/Container';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Text from '@zg-rentals/multibrand-ui/lib/Text';

import Autocomplete from '../../modules/Autocomplete';
import MoveInFeedbackActions from '../../../flux/actions/MoveInFeedbackActions';
import AutocompleteActions from '../../../flux/actions/AutocompleteActions';
import AuthCheckWrapper from '../../modules/AuthCheckWrapper';
import queryUtil from '../../../utils/queryUtils';
import pathsManifest from '../../../pathsManifest';
import { logError } from '@zg-rentals/log-error';

export default function MoveInFeedbackPage() {
  const initialValues = {
    notFoundInListings: false,
    selectedListingIndex: -2,
    provideAddress: true,
    fullAddress: '',
  };

  const dispatch = useDispatch();
  const [selectListing, setSelectListing] = useState(initialValues);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const updateAddress = (value) => {
    setShowErrorMessage(false);
    setSelectListing((prevState) => {
      return {
        ...prevState,
        fullAddress: value,
      };
    });
  };

  const { addressList, listings, routerLocation } = useSelector((state) => ({
    addressList: state.autocompleteAddress.addressList,
    listings: state.moveInFeedback.listings,
    routerLocation: state.router.location,
  }));

  const handleTriggerAction = () => {
    dispatch(AutocompleteActions.autocomplete(selectListing.fullAddress));
  };

  const getSuccessPageQueryParam = (isMultiFamily) => {
    const query = assign({}, queryUtil.parse(routerLocation.search), {
      isMultiFamily,
    });
    return queryUtil.stringify(query);
  };

  const handleSubmitBtn = () => {
    const currentQuery = queryUtil.parse(routerLocation.search);
    const { selectedListingIndex, fullAddress } = selectListing;

    // no address is selected and no manually typed address
    if (selectedListingIndex === -2) {
      setSelectListing((prevState) => {
        return {
          ...prevState,
          provideAddress: false,
        };
      });
    }

    const payload = {
      renterId: currentQuery.renterId,
      listingAlias:
        selectedListingIndex === -1 || selectedListingIndex === -2 ? null : listings[selectedListingIndex].listingAlias,
      fullAddress: fullAddress,
      inquiryId: null,
    };

    dispatch(MoveInFeedbackActions.submitFeedback(payload))
      .then((response = {}) => {
        if (response && response.success) {
          //redirect
          const isMultiFamily =
            selectedListingIndex === -1 || selectedListingIndex === -2
              ? false
              : listings[selectedListingIndex].isMultiFamily;
          const queryString = getSuccessPageQueryParam(isMultiFamily);
          dispatch(push(pathsManifest.moveInFeedbackSuccessPage + queryString));
        } else {
          if (selectedListingIndex !== -2) {
            setShowErrorMessage(true);
          }
        }
        return;
      })
      .catch((err) => {
        logError({
          error: err,
          errorType: 'InquiryFeedbackPage',
          context: { location: 'InquiryFeedbackPage#handleSubmitBtn' },
        });
      });
  };

  const handleSelectOptions = () => {
    const e = document.getElementById('source-selection');
    const selectedValue = e.options[e.selectedIndex].value;
    const newListingObject = {
      provideAddress: true,
      notFoundInListings: false,
      fullAddress: '',
    };

    setShowErrorMessage(false);

    if (selectedValue === 'other') {
      newListingObject.selectedListingIndex = -1;
      newListingObject.notFoundInListings = true;
    } else if (selectedValue === 'empty') {
      newListingObject.selectedListingIndex = -2;
    } else {
      const selectedIndex = parseInt(selectedValue, 10);
      newListingObject.selectedListingIndex = selectedIndex;
      newListingObject.fullAddress = listings[selectedIndex].address;
    }

    setSelectListing((prevState) => {
      return {
        ...prevState,
        ...newListingObject,
      };
    });
  };

  useEffect(() => {
    const currentQuery = queryUtil.parse(routerLocation.search);
    const { renterId, latestInquiredDateInMillis } = currentQuery;
    if (renterId && latestInquiredDateInMillis) {
      dispatch(MoveInFeedbackActions.getListingsByRenterId(renterId, latestInquiredDateInMillis))
        .then((retListings) => {
          return dispatch(MoveInFeedbackActions.setListings(retListings));
        })
        .catch((err) => {
          logError({
            error: err,
            errorType: 'MoveInFeedbackPage',
            context: { location: 'MoveInFeedbackPage#useEffect' },
          });
        });
    }
  }, []);

  return (
    <AuthCheckWrapper requireStrongAuthentication={false}>
      <div>
        <Helmet>
          <title>Move In Feedback</title>
        </Helmet>
        <Container className="stacked-children-sm" style={{ marginBottom: '300px', marginTop: '30px' }}>
          <Row>
            <Col>
              <Text type="h1" emphasize>
                Found your new place?
              </Text>
            </Col>
          </Row>
          <Row>
            <Col>
              <Text type="body">We&apos;re excited you found your new home. Let us know where you moved in.</Text>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={9} lg={7}>
              <Select onChange={handleSelectOptions} id="source-selection">
                <option key="empty" value="empty">
                  Select an option
                </option>
                {listings &&
                  listings.map((listing, idx) => (
                    <option key={`listing-${idx}`} value={idx}>
                      {listing.address}
                    </option>
                  ))}
                <option key="other" value="other">
                  Other - cannot find my listing above
                </option>
              </Select>
              {!selectListing.provideAddress && (
                <Text type="body-small" style={{ color: 'red' }}>
                  Please select a valid address.
                </Text>
              )}
            </Col>
          </Row>
          {selectListing.notFoundInListings && (
            <Row className="stacked-children-md">
              <Col>
                <Text type="body">All right, no problem. Type the address below:</Text>
              </Col>
              <Col xs={12} md={9} lg={7}>
                <Autocomplete
                  className="Listings-autocomplete"
                  itemList={addressList}
                  placeholder="Please enter your address..."
                  value={selectListing.fullAddress}
                  onChange={updateAddress}
                  onMouseDown={updateAddress}
                  onTriggerAction={handleTriggerAction}
                />
              </Col>
            </Row>
          )}
          {showErrorMessage && (
            <Text type="body-small" style={{ color: 'red', paddingLeft: '8px' }}>
              Error submitting the form.
            </Text>
          )}
          <Row>
            <Col xs={12} sm={1}>
              <Button type="primary" full onClick={handleSubmitBtn}>
                Submit
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    </AuthCheckWrapper>
  );
}
